import React, { createContext, ReactNode, useContext, useState } from 'react'
import { SerializedContent } from '@/features/screenplay/components/editor/utils/JsonUtils'

interface StAIWriterChatContextType {
    isDrawerOpen: boolean
    openDrawer: (mode: AIChatMode, inputNode?: SerializedContent[]) => void
    inputNodes: SerializedContent[] | undefined
    setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
    title: string
    mode: AIChatMode
    outputNodes: SerializedContent[]
    setOutputNodes: React.Dispatch<SerializedContent[]>
}

export enum AIChatMode {
    create_story = 'create_story',
    edit_story = 'edit_story',
}

type AIChatModeMeta = {
    title: string
    child: React.ReactNode
    promptPlaceholder: string
}

export const aIChatModeMetaData: Record<AIChatMode, AIChatModeMeta> = {
    [AIChatMode.create_story]: {
        title: 'Create',
        child: undefined,
        promptPlaceholder: 'Describe the story you want to create… a setting, a vibe, a moment.',
    },
    [AIChatMode.edit_story]: {
        title: 'Update',
        child: undefined,
        promptPlaceholder: 'Tell us what to adjust — tone, pacing, dialogue, character intent…',
    },
}
const StAIWriterChatContext = createContext<StAIWriterChatContextType | undefined>(undefined)
export const StAIWriterChatProvider = ({ children }: { children: ReactNode }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [outputNodes, setOutputNodes] = useState<SerializedContent[]>([])
    const [mode, setMode] = useState<AIChatMode>(AIChatMode.create_story)
    const [title, setTitle] = useState('')
    const [inputNodes, setInputNodes] = useState<SerializedContent[] | undefined>(undefined)

    const openDrawer = (mode: AIChatMode, inputNode?: SerializedContent[]) => {
        setMode(mode)
        setTitle(aIChatModeMetaData[mode].title)
        setInputNodes(inputNode)
        setIsDrawerOpen(true)
        setOutputNodes([]) // Reset output nodes when opening the drawer
    }

    return (
        <StAIWriterChatContext.Provider
            value={{
                isDrawerOpen,
                openDrawer,
                inputNodes,
                setIsDrawerOpen,
                title,
                mode,
                outputNodes,
                setOutputNodes,
            }}
        >
            {children}
        </StAIWriterChatContext.Provider>
    )
}

export const useStAIWriterChat = () => {
    const context = useContext(StAIWriterChatContext)
    if (!context) {
        throw new Error('useStAIWriterChat must be used within a StAIWriterChatProvider')
    }
    return context
}
